/**
 * QMS Policy Service (Phase 1)
 * Single domain service for policy_documents, policy_structure, and requirements.
 * Used for regulatory document management and requirement extraction (ORDIN 1761, etc.).
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';

const DOCUMENTS = 'policy_documents';
const STRUCTURE = 'policy_structure';
const REQUIREMENTS = 'requirements';

function removeUndefinedValues(obj) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefinedValues);
  if (typeof obj !== 'object') return obj;
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) cleaned[key] = removeUndefinedValues(value);
  }
  return cleaned;
}

class QmsPolicyService {
  constructor() {
    this.documentsRef = collection(db, DOCUMENTS);
    this.structureRef = collection(db, STRUCTURE);
    this.requirementsRef = collection(db, REQUIREMENTS);
  }

  // ---------- Policy Documents ----------

  async getPolicyDocuments(filters = {}) {
    let q;
    if (filters.organizationId !== undefined) {
      q = query(
        this.documentsRef,
        where('organizationId', '==', filters.organizationId),
        orderBy('code', 'asc')
      );
    } else if (filters.status) {
      q = query(this.documentsRef, where('status', '==', filters.status), orderBy('code', 'asc'));
    } else {
      q = query(this.documentsRef, orderBy('code', 'asc'));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async getPolicyDocument(documentId) {
    const ref = doc(this.documentsRef, documentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Policy document not found');
    return { id: snap.id, ...snap.data() };
  }

  async createPolicyDocument(data, userId, userName) {
    if (!data.code || !data.title) throw new Error('Code and title are required');
    const now = Timestamp.now();
    const payload = removeUndefinedValues({
      organizationId: data.organizationId ?? null,
      code: data.code,
      title: data.title,
      issuingAuthority: data.issuingAuthority || '',
      documentType: data.documentType || 'ordinance',
      legalForce: data.legalForce || 'mandatory',
      effectiveDate: data.effectiveDate || now,
      status: data.status || 'active',
      policyFrameworkId: data.policyFrameworkId || null,
      policyFrameworkName: data.policyFrameworkName || null,
      createdAt: now,
      createdBy: userId,
      createdByName: userName || userId,
      updatedAt: now,
      updatedBy: userId,
      updatedByName: userName || userId,
    });
    const ref = await addDoc(this.documentsRef, payload);
    return ref.id;
  }

  async updatePolicyDocument(documentId, updates, userId, userName) {
    const ref = doc(this.documentsRef, documentId);
    const payload = removeUndefinedValues({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
      updatedByName: userName || userId,
    });
    await updateDoc(ref, payload);
    return documentId;
  }

  // ---------- Policy Structure (tree) ----------

  async getStructureNodes(policyDocumentId) {
    const q = query(
      this.structureRef,
      where('policyDocumentId', '==', policyDocumentId),
      orderBy('orderIndex', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async getStructureTree(policyDocumentId) {
    const nodes = await this.getStructureNodes(policyDocumentId);
    const byId = {};
    nodes.forEach((n) => { byId[n.id] = { ...n, children: [] }; });
    const roots = [];
    nodes.forEach((n) => {
      const node = byId[n.id];
      if (!n.parentId) roots.push(node);
      else if (byId[n.parentId]) byId[n.parentId].children.push(node);
      else roots.push(node);
    });
    const sort = (arr) => arr.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    sort(roots);
    roots.forEach((r) => this._sortChildren(r, sort));
    return roots;
  }

  _sortChildren(node, sort) {
    if (node.children && node.children.length) {
      sort(node.children);
      node.children.forEach((c) => this._sortChildren(c, sort));
    }
  }

  buildMaterializedPath(parentPath, orderIndex) {
    const segment = parentPath ? `${parentPath}_${orderIndex}` : `${orderIndex}`;
    return segment.startsWith('/') ? segment : `/${segment}`;
  }

  async createStructureNode(data, userId, userName) {
    if (!data.policyDocumentId) throw new Error('policyDocumentId is required');
    const parentPath = data.parentPath ?? '';
    const path = this.buildMaterializedPath(parentPath, data.orderIndex ?? 0);
    const now = Timestamp.now();
    const payload = removeUndefinedValues({
      policyDocumentId: data.policyDocumentId,
      parentId: data.parentId ?? null,
      nodeType: data.nodeType || 'section',
      nodeNumber: data.nodeNumber || '',
      nodeTitle: data.nodeTitle || '',
      nodeContent: data.nodeContent || '',
      materializedPath: path,
      orderIndex: data.orderIndex ?? 0,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
    });
    const ref = await addDoc(this.structureRef, payload);
    return ref.id;
  }

  async updateStructureNode(nodeId, updates, userId, userName) {
    const ref = doc(this.structureRef, nodeId);
    const payload = removeUndefinedValues({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });
    await updateDoc(ref, payload);
    return nodeId;
  }

  async getStructureNode(nodeId) {
    const ref = doc(this.structureRef, nodeId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Policy structure node not found');
    return { id: snap.id, ...snap.data() };
  }

  // ---------- Requirements ----------

  async getRequirementsByPolicy(policyDocumentId) {
    const q = query(
      this.requirementsRef,
      where('policyDocumentId', '==', policyDocumentId),
      orderBy('code', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async getRequirementsBySourceNode(sourcePolicyStructureId) {
    const q = query(
      this.requirementsRef,
      where('sourcePolicyStructureId', '==', sourcePolicyStructureId),
      orderBy('code', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async getRequirement(requirementId) {
    const ref = doc(this.requirementsRef, requirementId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Requirement not found');
    return { id: snap.id, ...snap.data() };
  }

  async createRequirement(data, userId, userName) {
    if (!data.policyDocumentId || !data.code || !data.requirementText) {
      throw new Error('policyDocumentId, code and requirementText are required');
    }
    const now = Timestamp.now();
    const payload = removeUndefinedValues({
      policyDocumentId: data.policyDocumentId,
      sourcePolicyStructureId: data.sourcePolicyStructureId ?? null,
      code: data.code,
      requirementText: data.requirementText,
      complianceLevel: data.complianceLevel || 'MUST',
      targetAudience: data.targetAudience || '',
      verificationMethod: data.verificationMethod || '',
      deviationThresholds: data.deviationThresholds ?? null,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId,
    });
    const ref = await addDoc(this.requirementsRef, payload);
    return ref.id;
  }

  async updateRequirement(requirementId, updates, userId, userName) {
    const ref = doc(this.requirementsRef, requirementId);
    const payload = removeUndefinedValues({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });
    await updateDoc(ref, payload);
    return requirementId;
  }
}

export default new QmsPolicyService();
